/**
 * Utility functions and types for the embeddable chat widget.
 */

// Tracking data sent from the parent page to the iframe via postMessage
export type TrackingMessage = {
  type: 'heidi:tracking';
  firstTouch: Record<string, unknown> | null;
  lastTouch: Record<string, unknown> | null;
  currentParams: Record<string, unknown> | null;
  referrer: string | null;
  landingPage: string | null;
};

// Message types for postMessage communication
export type WidgetMessage =
  | { type: 'heidi:ready' }
  | { type: 'heidi:resize'; height: number }
  | { type: 'heidi:chatCreated'; chatId: string }
  | { type: 'heidi:messageSent'; message: string }
  | { type: 'heidi:messageReceived'; message: string }
  | { type: 'heidi:error'; error: string }
  | {
      type: 'heidi:redirect';
      url: string;
      target?: '_self' | '_blank' | '_parent' | '_top';
    }
  | { type: 'heidi:login' }
  | { type: 'heidi:signup' }
  | { type: 'heidi:modalOpened' }
  | { type: 'heidi:modalClosed' }
  | {
      // Sent on every user message dispatch from the Evidence chat. The host
      // page filters on isAuthenticated === false && isFirstMessage === true
      // to fire a Lead conversion in GTM (Google Ads + Meta), with messageId
      // serving as the dedup key (Order ID / eventID).
      type: 'heidi:evidence_message_sent';
      isAuthenticated: boolean;
      isFirstMessage: boolean;
      messageId: string;
      sentAt: number;
    };

/**
 * Validates if a postMessage origin is allowed.
 * Uses NEXT_PUBLIC_WIDGET_ALLOWED_ORIGINS env var if set (comma-separated),
 * otherwise falls back to default Heidi domains.
 */
export const isAllowedOrigin = (origin: string): boolean => {
  // In development, allow all origins for easier testing
  if (process.env.NODE_ENV === 'development') {
    return true;
  }

  // Check env var first (comma-separated list)
  const envOrigins = process.env.NEXT_PUBLIC_WIDGET_ALLOWED_ORIGINS;
  if (envOrigins) {
    const allowedList = envOrigins.split(',').map((o) => o.trim());
    return allowedList.some((allowed) => {
      // Support wildcard subdomains (e.g., "*.vercel.app")
      if (allowed.startsWith('*.')) {
        const domain = allowed.slice(2);
        return origin.endsWith(domain) || origin.endsWith(`.${domain}`);
      }
      return origin === allowed;
    });
  }

  // Default allowed origins
  const defaultOrigins = [
    'https://heidihealth.com',
    'https://www.heidihealth.com',
    'https://heidi.ai',
    'https://www.heidi.ai',
    'https://scribe.dev.heidihealth.com',
    'https://scribe.heidihealth.com',
    'https://staging-phoenix-rising.vercel.app',
  ];

  return defaultOrigins.includes(origin);
};
